import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PROFESSIONAL_ROLES } from "@/lib/vg2NetworkApplications";
import ConfirmModal from "@/components/ConfirmModal";
import { ArrowLeft, Eye, Trash2 } from "lucide-react";

const STATUS_LABEL = {
  SUBMITTED: "Ricevuta",
  UNDER_REVIEW: "In revisione",
  ADMITTED: "Ammesso",
  REJECTED: "Rifiutata",
};

const STATUS_STYLE = {
  SUBMITTED: "bg-[#F5F5F5] text-[#0A0A0A] border-[#E5E5E5]",
  UNDER_REVIEW: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  ADMITTED: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  REJECTED: "bg-[#FEF2F2] text-[#7F1D1D] border-[#FECACA]",
};

function StatusBadge({ status }) {
  return (
    <span
      data-testid={`na-status-${status}`}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_STYLE[status] || ""}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function ApplicationDetail({ id, onClose, onChanged }) {
  const [app, setApp] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/v2/admin/network-applications/${id}`)
      .then((r) => setApp(r.data))
      .catch(() => toast.error("Errore nel caricamento della candidatura"));
  }, [id]);

  const doStartReview = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v2/admin/network-applications/${id}/start-review`);
      setApp(r.data);
      onChanged?.();
      toast.success("Revisione avviata");
    } catch (e) {
      toast.error("Impossibile avviare la revisione");
    } finally { setBusy(false); }
  };

  const doAdmit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v2/admin/network-applications/${id}/admit`);
      setApp((a) => ({ ...a, status: "ADMITTED", admitted_network_professional_id: r.data.admitted_network_professional_id }));
      setAdmitOpen(false);
      onChanged?.();
      toast.success("Candidato ammesso nella rete estesa");
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code;
      toast.error(code === "STATE_INVALID_TRANSITION"
        ? "Transizione non consentita in questo stato"
        : "Errore durante l'ammissione");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (rejectReason.trim().length < 6) {
      toast.error("Motivazione richiesta (min 6 caratteri)");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/v2/admin/network-applications/${id}/reject`, {
        reason: rejectReason.trim(),
      });
      setApp(r.data);
      setRejectOpen(false);
      setRejectReason("");
      onChanged?.();
      toast.success("Candidatura rifiutata");
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code;
      toast.error(code === "STATE_INVALID_TRANSITION"
        ? "Transizione non consentita"
        : "Errore durante il rifiuto");
    } finally { setBusy(false); }
  };

  if (!app) return <div className="p-8 text-[#0A0A0A]">Caricamento…</div>;

  return (
    <div className="max-w-4xl">
      <button
        onClick={onClose}
        className="text-[13px] text-[#B40101] font-medium mb-4 inline-flex items-center gap-1 hover:underline"
        data-testid="na-detail-back"
      >
        <ArrowLeft size={14} /> Torna alla lista
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-2xl font-semibold text-[#0A0A0A]">
              {app.business_name || `${app.first_name} ${app.last_name}`}
            </h2>
            <StatusBadge status={app.status} />
          </div>
          <p className="text-[13px] text-[#4B5563]">
            {app.first_name} {app.last_name} · {app.email} · {app.phone}
          </p>
          <p className="text-[13px] text-[#4B5563]">
            ID: <code className="font-mono">{app.application_id}</code>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Info label="Ruolo professionale">{app.professional_role}</Info>
        <Info label="Città base">{app.city}</Info>
        <Info label="Aree di copertura">{(app.coverage_areas || []).join(", ")}</Info>
        <Info label="Sito / profilo">{app.website || "—"}</Info>
        <Info label="Albo / Ordine">{app.registration_body || "—"}</Info>
        <Info label="N. iscrizione">{app.registration_number || "—"}</Info>
      </div>

      <Info label="Descrizione professionale">
        <p className="whitespace-pre-wrap">{app.professional_description}</p>
      </Info>
      <div className="h-4" />
      <Info label="Motivazione">
        <p className="whitespace-pre-wrap">{app.motivation}</p>
      </Info>

      <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
        <p className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-3 font-semibold">
          Metadata interna
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px]">
          <div>Inviata: <code className="font-mono">{app.submitted_at}</code></div>
          <div>Aggiornata: <code className="font-mono">{app.updated_at}</code></div>
          <div>Reviewer: <code className="font-mono">{app.reviewer_user_id || "—"}</code></div>
          <div>Rev. avviata: <code className="font-mono">{app.review_started_at || "—"}</code></div>
          <div>Rev. completata: <code className="font-mono">{app.reviewed_at || "—"}</code></div>
          <div>Prof. ammesso: <code className="font-mono">{app.admitted_network_professional_id || "—"}</code></div>
        </div>
        {app.internal_review_reason && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1 font-semibold">
              Motivo rifiuto (interno)
            </p>
            <p className="text-[14px] text-[#0A0A0A] whitespace-pre-wrap">{app.internal_review_reason}</p>
          </div>
        )}
      </div>

      <div className="mt-8 pt-6 border-t border-[#E5E5E5] flex flex-wrap gap-3">
        {app.status === "SUBMITTED" && (
          <button
            onClick={doStartReview}
            disabled={busy}
            data-testid="na-action-start-review"
            className="bg-[#404040] text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
          >
            Avvia revisione
          </button>
        )}
        {app.status === "UNDER_REVIEW" && (
          <button
            onClick={() => setAdmitOpen(true)}
            disabled={busy}
            data-testid="na-action-admit"
            className="bg-[#B40101] hover:bg-[#8F0101] text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
          >
            Ammetti nella rete
          </button>
        )}
        {["SUBMITTED", "UNDER_REVIEW"].includes(app.status) && (
          <button
            onClick={() => setRejectOpen(true)}
            disabled={busy}
            data-testid="na-action-reject"
            className="border border-[#B40101] text-[#B40101] px-4 py-2 rounded-lg font-medium disabled:opacity-50"
          >
            Rifiuta
          </button>
        )}
      </div>

      {admitOpen && (
        <ConfirmModal
          open={admitOpen}
          testid="na-modal-admit"
          title="Ammettere il candidato nella rete estesa?"
          message="L'operazione crea un professionista nella VG Extended Network. Non crea alcun Circle né una assegnazione automatica."
          confirmLabel="Ammetti"
          onCancel={() => setAdmitOpen(false)}
          onConfirm={doAdmit}
        />
      )}

      {rejectOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="na-modal-reject">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-3">Rifiuta candidatura</h3>
            <p className="text-[13px] text-[#4B5563] mb-4">
              La motivazione è interna: non viene mai mostrata al candidato.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo interno (min 6 caratteri)"
              maxLength={600}
              rows={4}
              data-testid="na-reject-reason"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[#B40101]"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setRejectOpen(false); setRejectReason(""); }}
                className="px-4 py-2 rounded-lg border border-[#E5E5E5]"
                data-testid="na-reject-cancel"
              >
                Annulla
              </button>
              <button
                onClick={doReject}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-[#B40101] text-white font-semibold disabled:opacity-50"
                data-testid="na-reject-confirm"
              >
                Conferma rifiuto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1 font-semibold">
        {label}
      </div>
      <div className="text-[14px] text-[#0A0A0A]">{children}</div>
    </div>
  );
}

export default function AdminNetworkApplications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (status) params.status = status;
    if (role) params.professional_role = role;
    api.get("/v2/admin/network-applications", { params })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => toast.error("Errore caricamento candidature"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [status, role]);

  const allSelected = items.length > 0 && items.every((it) => selectedIds.has(it.application_id));
  const toggleOne = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((it) => it.application_id)));

  async function confirmDeleteOne() {
    if (!pendingDelete) return;
    setDelBusy(true);
    try {
      await api.delete(`/v2/admin/network-applications/${pendingDelete.application_id}`);
      toast.success("Candidatura eliminata");
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(pendingDelete.application_id); return n; });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error_code || "Eliminazione non riuscita");
    } finally { setDelBusy(false); setPendingDelete(null); }
  }

  async function confirmBulkDelete() {
    setDelBusy(true);
    try {
      const r = await api.post("/v2/admin/network-applications/bulk-delete", { application_ids: Array.from(selectedIds) });
      toast.success(`Eliminate ${r.data.deleted_count} candidature`);
      setSelectedIds(new Set());
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error_code || "Eliminazione in blocco non riuscita");
    } finally { setDelBusy(false); setBulkConfirm(false); }
  }

  if (selectedId) {
    return (
      <ApplicationDetail
        id={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={reload}
      />
    );
  }

  return (
    <div data-testid="admin-network-applications">
      <div className="mb-6 flex flex-wrap items-end gap-4 justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">
            Candidature professionisti
          </h1>
          <p className="text-[13px] text-[#4B5563] mt-1">
            Rete professionale ValutaGenio · {total} candidature
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="na-filter-status"
            className="border border-[#E5E5E5] rounded-lg px-3 py-2 text-[13px] bg-white"
          >
            <option value="">Tutti gli stati</option>
            <option value="SUBMITTED">Ricevuta</option>
            <option value="UNDER_REVIEW">In revisione</option>
            <option value="ADMITTED">Ammesso</option>
            <option value="REJECTED">Rifiutata</option>
          </select>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="na-filter-role"
            className="border border-[#E5E5E5] rounded-lg px-3 py-2 text-[13px] bg-white"
          >
            <option value="">Tutti i ruoli</option>
            {PROFESSIONAL_ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between gap-3" data-testid="na-bulk-toolbar">
        <span className="text-[13px] text-[#4B5563]" data-testid="na-selected-count">
          {selectedIds.size} selezionate
        </span>
        <button
          type="button"
          onClick={() => setBulkConfirm(true)}
          disabled={selectedIds.size === 0}
          className="inline-flex items-center gap-2 text-[12px] px-3 py-2 rounded-md bg-[#B40101] text-white disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="na-bulk-delete-btn"
        >
          <Trash2 size={14} /> Elimina selezionate
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
        <table className="w-full text-[13px]" data-testid="na-list-table">
          <thead className="bg-[#F5F5F5] text-[#4B5563] uppercase tracking-wider text-[11px] font-semibold">
            <tr>
              <th className="text-left px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Seleziona tutte"
                  data-testid="na-select-all"
                />
              </th>
              <th className="text-left px-4 py-3">Ricevuta</th>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Ruolo</th>
              <th className="text-left px-4 py-3">Città</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-[#4B5563]">Caricamento…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-[#4B5563]">Nessuna candidatura</td></tr>
            )}
            {items.map((it) => (
              <tr
                key={it.application_id}
                className="border-t border-[#E5E5E5] hover:bg-[#FAFAFA]"
                data-testid={`na-row-${it.application_id}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(it.application_id)}
                    onChange={() => toggleOne(it.application_id)}
                    aria-label={`Seleziona ${it.application_id}`}
                    data-testid={`na-select-${it.application_id}`}
                  />
                </td>
                <td className="px-4 py-3 text-[#0A0A0A]/70">
                  {new Date(it.submitted_at).toLocaleDateString("it-IT")}
                </td>
                <td className="px-4 py-3 text-[#0A0A0A]">
                  <div className="font-medium">{it.business_name || `${it.first_name} ${it.last_name}`}</div>
                  {it.business_name && (
                    <div className="text-[12px] text-[#4B5563]">{it.first_name} {it.last_name}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-[#0A0A0A]/80">{it.professional_role}</td>
                <td className="px-4 py-3 text-[#0A0A0A]/80">{it.city}</td>
                <td className="px-4 py-3 text-[#0A0A0A]/80">{it.email}</td>
                <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button
                      onClick={() => setSelectedId(it.application_id)}
                      className="text-[#B40101] hover:opacity-70"
                      aria-label="Dettaglio"
                      data-testid={`na-row-view-${it.application_id}`}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => setPendingDelete(it)}
                      className="text-[#B40101] hover:opacity-70"
                      aria-label="Elimina candidatura"
                      data-testid={`na-row-delete-${it.application_id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={!!pendingDelete}
        title="Elimina candidatura"
        message={pendingDelete ? (
          <span data-testid="na-delete-modal-body">
            Stai per eliminare la candidatura di{" "}
            <strong>{pendingDelete.business_name || `${pendingDelete.first_name} ${pendingDelete.last_name}`}</strong>
            {" "}({pendingDelete.email}).
            <span className="block mt-3">
              Viene rimosso solo il record della candidatura. Nessun account, caso o
              professionista già ammesso nella rete viene toccato.
            </span>
            <span className="block mt-3 font-semibold text-[#B40101]">L'operazione non è reversibile.</span>
          </span>
        ) : ""}
        confirmLabel={delBusy ? "Eliminazione…" : "Conferma elimina"}
        destructive
        onConfirm={confirmDeleteOne}
        onCancel={() => setPendingDelete(null)}
        testid="na-delete-modal"
      />

      <ConfirmModal
        open={bulkConfirm}
        title={`Elimina ${selectedIds.size} candidature selezionate`}
        message={(
          <span data-testid="na-bulk-modal-body">
            Stai per eliminare <strong>{selectedIds.size}</strong> candidature. Viene rimosso solo
            il record di ciascuna candidatura; account, casi e professionisti già ammessi nella
            rete non vengono toccati.
            <span className="block mt-3 font-semibold text-[#B40101]">L'operazione non è reversibile.</span>
          </span>
        )}
        confirmLabel={delBusy ? "Eliminazione…" : "Conferma elimina selezionate"}
        destructive
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkConfirm(false)}
        testid="na-bulk-delete-modal"
      />
    </div>
  );
}
